import React, { useContext, useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Avatar,
  Menu,
  MenuItem,
  IconButton,
  Tooltip
} from '@mui/material';
import { API_BASE_URL } from '../config/api';
import {
  ExitToApp,
  LightMode,
  DarkMode,
  SettingsBrightness,
  Menu as MenuIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { ThemeModeContext } from '../index';
import logger from '../utils/logger';

const Header = ({ isMobile, onMenuClick }) => {
  const { user, isAuthenticated, login, logout, loading } = useAuth();
  const { themeMode, actualMode, toggleTheme } = useContext(ThemeModeContext);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [userPhoto, setUserPhoto] = useState(null);
  
  // Determine if we're in dark mode
  const isDarkMode = actualMode === 'dark';

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Fetch user photo from Microsoft Graph when authenticated
  useEffect(() => {
    const fetchUserPhoto = async () => {
      // Revoke previous photo URL if exists
      if (userPhoto) {
        URL.revokeObjectURL(userPhoto);
        setUserPhoto(null);
      }
      
      if (!isAuthenticated) {
        return;
      }
      
      try {
        // Add cache-busting query param based on user id
        const cacheBuster = user?.id || Date.now();
        const response = await fetch(`${API_BASE_URL}/auth/photo?cb=${cacheBuster}`, { credentials: 'include' });
        if (response.ok) {
          const blob = await response.blob();
          const imageUrl = URL.createObjectURL(blob);
          setUserPhoto(imageUrl);
        }
      } catch (error) {
        logger.log('Could not fetch user photo:', error);
      }
    };
    
    fetchUserPhoto();
    
    // Cleanup blob URL on unmount
    return () => {
      if (userPhoto) {
        URL.revokeObjectURL(userPhoto);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.id]);

  const handleLogin = async () => {
    try {
      await login();
    } catch (error) {
      logger.error('Login failed:', error);
    }
  };

  const handleLogout = () => {
    logout();
    handleMenuClose();
  };

  return (
    <AppBar 
      position="static" 
      elevation={1} 
      sx={{ 
        backgroundColor: 'background.paper', 
        color: 'text.primary',
        borderBottom: (theme) => `1px solid ${theme.palette.divider}`
      }}
    >
      <Toolbar>
        {/* Mobile Menu Button */}
        {isMobile && (
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={onMenuClick}
            sx={{ mr: 1 }}
          >
            <MenuIcon />
          </IconButton>
        )}
        
        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, gap: 1.5 }}>
          <img 
            src="/logo512.png" 
            alt="IntuneMermaid Logo" 
            style={{ 
              height: isMobile ? 28 : 36, 
              width: isMobile ? 28 : 36,
              objectFit: 'contain'
            }} 
          />
          <Typography 
            variant="h5" 
            component="div" 
            sx={{ 
              fontWeight: 700,
              fontSize: isMobile ? '1.25rem' : '1.75rem',
              lineHeight: 1,
              background: 'linear-gradient(90deg, #00B4D8 0%, #7B68EE 50%, #D838F7 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            {isMobile ? 'IntuneMermaid' : 'Intune Mermaid'}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 1 : 2 }}>
          {/* Theme Toggle */}
          <Tooltip title={`Theme: ${themeMode === 'system' ? 'System' : themeMode === 'light' ? 'Light' : 'Dark'} (click to cycle)`}>
            <IconButton
              size="small"
              onClick={toggleTheme}
              color="inherit"
            >
              {themeMode === 'system' && <SettingsBrightness />}
              {themeMode === 'light' && <LightMode />}
              {themeMode === 'dark' && <DarkMode />}
            </IconButton>
          </Tooltip>

          {/* User Authentication */}
          {isAuthenticated ? (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar
                  src={userPhoto}
                  sx={{ 
                    width: isMobile ? 28 : 32, 
                    height: isMobile ? 28 : 32, 
                    bgcolor: 'transparent',
                    color: 'text.primary',
                    cursor: 'pointer' 
                  }}
                  onClick={handleMenuOpen}
                >
                  {!userPhoto && (user?.displayName ? user.displayName.charAt(0).toUpperCase() : 'U')}
                </Avatar>
                <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {user?.displayName || 'User'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {user?.mail || user?.userPrincipalName || ''}
                  </Typography>
                </Box>
              </Box>

              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              >
                <MenuItem onClick={handleLogout}>
                  <ExitToApp sx={{ mr: 2 }} />
                  Logout
                </MenuItem>
              </Menu>
            </>
          ) : (
            <Button
              variant="outlined"
              onClick={handleLogin}
              disabled={loading}
              sx={{ 
                textTransform: 'none',
                // Microsoft branding: dark theme for dark mode, light theme for light mode
                backgroundColor: isDarkMode ? '#2f2f2f' : '#fff',
                color: isDarkMode ? '#fff' : '#5e5e5e',
                border: isDarkMode ? '1px solid #8c8c8c' : '1px solid #8c8c8c',
                borderRadius: '4px',
                fontFamily: '"Segoe UI", system-ui, sans-serif',
                fontSize: isMobile ? '13px' : '15px',
                fontWeight: 600,
                height: isMobile ? '36px' : '41px',
                px: isMobile ? 1.5 : 2,
                minWidth: isMobile ? 'auto' : undefined,
                '&:hover': {
                  backgroundColor: isDarkMode ? '#3f3f3f' : '#f5f5f5',
                  border: '1px solid #8c8c8c',
                },
                '&:disabled': {
                  backgroundColor: isDarkMode ? '#2f2f2f' : '#fff',
                  color: '#8c8c8c',
                }
              }}
            >
              <Box
                component="span"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: isMobile ? 1 : 1.5,
                  whiteSpace: 'nowrap',
                }}
              >
                {/* Microsoft Logo */}
                <svg xmlns="http://www.w3.org/2000/svg" width={isMobile ? "18" : "21"} height={isMobile ? "18" : "21"} viewBox="0 0 21 21">
                  <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
                  <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
                  <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
                  <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
                </svg>
                {loading ? (isMobile ? 'Signing in...' : 'Signing in...') : (isMobile ? 'Sign in' : 'Sign in with Microsoft')}
              </Box>
            </Button>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Header;